import { useEffect } from 'react';

type Image = {
  title?: string;
  url: string;
};
type CSSColorFromContentful = string;
export type BrandingColorKey = (typeof brandingContentfulKeys)[number];

export type BrandingQuery = {
  id: string;
  name: string;
  favicon: Image;
  logoSidebar: Image;
  logoContent: Image;
} & Record<BrandingColorKey, CSSColorFromContentful>;

const textHighlight = 'textHighlight';
const buttonText = 'buttonText';
const buttonBackground = 'buttonBackground';
const buttonHover = 'buttonHover';
const focusShade = 'focusShade';
const iconTint = 'iconTint';
const sidebarBackground = 'sidebarBackground';
const sidebarText = 'sidebarText';

const brandingContentfulKeys = [
  textHighlight,
  buttonText,
  buttonBackground,
  buttonHover,
  focusShade,
  iconTint,
  sidebarBackground,
  sidebarText,
] as const;

// To be used inside the shop contentful space
export const brandingQueryFragment = `
  fragment Branding on Branding {
    id
    name
    favicon {
      url
    }
    logoSidebar {
      url
    }
    logoContent {
      url
    }
    ${brandingContentfulKeys.join(`
    `)}
  }
`;

/** Colors to reset our branded "grey" / "highlight" colors to have a neutral version that works for all partners */
const brandingNeutralColors = {
  '--branding-neutral-background-50': '#F9F9F9',
  '--branding-neutral-background-60': '#F1F1F1',
  '--branding-neutral-white': '#ffffff',
  '--branding-neutral-dark-50': 'var(--dark-50)',
  '--branding-neutral-dark-60': 'var(--dark-60)',
  '--branding-neutral-dark-90': 'var(--dark-90)',
} as const;

/** Branding variables set to components within the frontend packages directory, don't use these in the funnel */
const brandingCssVariables: Record<string, BrandingColorKey> = {
  /* Global styles */
  '--branding-global-link': buttonBackground,

  /* Button from components and design-system */
  '--branding-button-primary-text': buttonText,
  '--branding-button-primary-background': buttonBackground,
  '--branding-button-primary-hover-background': buttonHover,
  '--branding-button-primary-focus-shadow': focusShade,
  '--branding-button-secundary-text': buttonBackground,
  '--branding-button-secundary-border': buttonBackground,
  '--branding-button-secundary-hover-background': focusShade,
  '--branding-button-secundary-focus-shadow': focusShade,
  '--branding-button-quarternary-background': sidebarBackground,
  '--branding-button-quarternary-text': sidebarText,

  /* Checkbox from design-system */
  '--branding-checkbox-background': buttonBackground,
  '--branding-checkbox-focus-shadow': focusShade,

  /* Radio from design-system */
  '--branding-radio-background': buttonBackground,
  '--branding-radio-focus-shadow': focusShade,

  /* funnel-components inputs */
  '--branding-base-input-focus-shadow': focusShade,
  '--branding-base-input-focus-border': buttonBackground,
  '--branding-input-select-focus-shadow': focusShade,
  '--branding-input-select-focus-border': buttonBackground,
  '--branding-input-money-focus-shadow': focusShade,
  '--branding-input-money-focus-border': buttonBackground,
  '--branding-input-radio-group-focus-shadow': focusShade,
  '--branding-input-radio-group-selected-border': buttonBackground,
  '--branding-input-checkbox-group-focus-shadow': focusShade,
  '--branding-input-checkbox-group-focus-border': buttonBackground,
  '--branding-input-checkbox-group-selected-background': buttonBackground,

  /* funnel-components other */
  '--branding-sidebar-background': sidebarBackground,
  '--branding-sidebar-text': sidebarText,
  '--branding-sidebar-indicator-text': buttonText,
  '--branding-sidebar-indicator-background': buttonBackground,
  '--branding-accordion-link': buttonBackground,
  '--branding-line-icon-tint': iconTint,
  '--branding-icon-stroke': buttonBackground,
  '--branding-tooltip-color': buttonBackground,
  '--branding-background-partnership-explained': focusShade,

  /* funnel-components page specific */
  '--branding-company-selected-border': buttonBackground,
  '--branding-company-focus-border': focusShade,
  '--branding-company-continue-icon': buttonBackground,
  '--branding-activity-search-add-icon': buttonBackground,
  '--branding-activity-suggestion-text': buttonBackground,
  '--branding-activity-suggestion-background': focusShade,
  '--branding-activity-tile-background': sidebarBackground,
  '--branding-activity-tile-text': sidebarText,
  '--branding-coverage-header-background': sidebarBackground,
  '--branding-coverage-header-text': sidebarText,
  '--branding-coverage-header-price-background': textHighlight,
  '--branding-coverage-overlay-text-highlight': textHighlight,
  '--branding-coverage-input-toggle-selected-border': buttonBackground,
  '--branding-coverage-input-toggle-selected-focus-shadow': focusShade,
  '--branding-coverage-receipt-price': textHighlight,
  '--branding-coverage-receipt-background': sidebarBackground,
  '--branding-coverage-receipt-text': sidebarText,
  '--branding-past-claims-add-button': buttonBackground,
  '--branding-confirmation-background': sidebarBackground,
  '--branding-confirmation-text': sidebarText,
  '--branding-price': textHighlight,
};

type BrandingQueryAndVariables = {
  /** Contentful query to apply on the CSS variables */
  query?: BrandingQuery;
  /** Variables that are funnel specific  */
  additionalVariables?: {
    [funnelVariable: string]: BrandingColorKey;
  };
};

export function useBrandingStyleVariables({ query, additionalVariables }: BrandingQueryAndVariables) {
  const root = document.body;

  useEffect(() => {
    if (query) {
      Object.entries(brandingNeutralColors).forEach(([cssVariable, color]) => {
        root.style.setProperty(cssVariable, color);
      });
      Object.entries({ ...brandingCssVariables, ...additionalVariables }).forEach(([cssVariable, brandingKey]) => {
        root.style.setProperty(cssVariable, query[brandingKey]);
      });
    }
  }, [query, root]);
}
